import {
  Button,
  type ButtonProps,
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ProgressBar from '@/components/shared/ProgressBar';
import type { AppointmentType } from '@/features/appointments/utils/AppointmentType';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useOrchestrateAppointmentFlow from '@/hooks/useOrchestrateAppointmentFlow';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

const SelectAppointmentType: React.FC = () => {
  const [tagged, setTagged] = useState(false);
  const { sessionId, sessionSource, flowType } = useSessionStore();
  const tagging = useTagging();
  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );

  const [selectedType, setSelectedType] = useState<AppointmentType | null>(
    selectedAppointmentType,
  );
  const { userSearch } = useAppointmentStore();

  useLandingPageRedirection();
  useProgress(20);

  const orchestrateAppointmentFlow = useOrchestrateAppointmentFlow();

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!tagged && sessionSource && sessionId) {
      const trvEndLocation = searchedDestination || userSearch || '';

      tagging.tag('view', sessionSource || 'new', {
        page: Page.appointmentOption,
        sessionid: sessionId,
        flow: flowType || '',
        trv_end_location: trvEndLocation,
      });

      setTagged(true);
    }
  }, [
    sessionId,
    flowType,
    sessionSource,
    tagging.tag,
    tagged,
    searchedDestination,
    userSearch,
  ]);

  const handleInPersonSelect = () => {
    setSelectedType('in-branch');
  };

  const handlePhoneCallSelect = () => {
    setSelectedType('online');
  };

  const handleClickContinue = () => {
    if (selectedType) {
      orchestrateAppointmentFlow(selectedType);
      navigate(`/schedule-appointment/${id}/date-selection`);
    }
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={6}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <Typography variant='h4'>How do you want to meet?</Typography>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={3}
          role='radiogroup'
          aria-label='Select appointment type'
        >
          <CustomTileButton
            startIcon={<Icon iconName='hotel-duotone' />}
            label='In person'
            onClick={handleInPersonSelect}
            selected={selectedType === 'in-branch'}
            data-testid='in-person-button'
          />
          <CustomTileButton
            startIcon={<Icon iconName='mobile-button-duotone' />}
            label='Phone call'
            onClick={handlePhoneCallSelect}
            selected={selectedType === 'online'}
            data-testid='phone-call-button'
          />
        </Stack>
      </Stack>
      <ProgressBar
        buttonDisabled={!selectedType}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

interface CustomTileButtonProps extends ButtonProps {
  selected?: boolean;
}

const CustomTileButton: React.FC<CustomTileButtonProps> = ({
  selected = false,
  ...props
}) => (
  <Button
    variant='tile'
    role='radio'
    aria-checked={selected}
    sx={[
      {
        backgroundColor: 'white',
        boxShadow:
          '0 -2px 1px 0 rgba(8, 17, 34, 0.04), 0 2px 4px 0 rgba(8, 17, 34, 0.20)',
        borderRadius: '8px',
        '&.MuiButton-tile': {
          width: 164,
          borderColor: 'white',
          gap: 1,
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'primary.50',
            borderColor: 'primary.400',
            transform: 'translateY(-1px)',
            boxShadow:
              '0 -4px 2px 0 rgba(8, 17, 34, 0.04), 0 4px 8px 0 rgba(8, 17, 34, 0.25)',
          },
        },
        '& svg': {
          width: 40,
          height: 40,
        },
        '& .MuiTypography-root': {
          fontWeight: 500,
        },
      },
      selected && {
        '&.MuiButton-tile': {
          borderColor: 'primary.main',
          borderWidth: 2,
          backgroundColor: 'primary.50',
          '&:hover': {
            backgroundColor: 'primary.100',
          },
        },
        '& svg': {
          color: 'primary.main',
        },
        '& .MuiTypography-root': {
          fontWeight: 600,
          color: 'primary.main',
        },
      },
    ]}
    {...props}
  />
);

export default SelectAppointmentType;
